import { Button as ButtonMui } from '@mui/material';
import styled from 'styled-components';

const GREEN = 'var(--bb-green, #1d966a)';
const GREEN_HOVER = 'var(--bb-green-hover, #187a56)';
const BLACK = 'var(--bb-black, #212529)';
const BORDER = '#e7e7e7';
const TEXT = '#606970';
const TITLE = '#4a4d50';
const MUTED = '#909499';
const TITLE_FONT =
  '\'Montserrat\', \'Open Sans\', \'Helvetica Neue\', Helvetica, Arial, sans-serif';

export const BBButton = styled(ButtonMui).attrs({ color: 'inherit' as const })`
  && {
    padding: 8px 24px;
    font-size: 14px;
    font-weight: 600;
    text-transform: none;
    border-radius: 4px;
    border: 1px solid ${GREEN};
    background: ${GREEN};
    color: #ffffff;
    min-height: auto;
    line-height: 1.5;
    box-shadow: none;
  }
  &&:hover {
    background: ${GREEN_HOVER};
    border-color: ${GREEN_HOVER};
    box-shadow: none;
  }
  && span {
    color: #ffffff;
  }
  &&.Mui-disabled {
    background: ${BORDER} !important;
    border-color: ${BORDER} !important;
    color: #9a9ea2 !important;
  }
  &&.Mui-disabled span {
    color: #9a9ea2 !important;
  }
`;

export const BBLink = styled.a`
  color: ${GREEN};
  text-decoration: underline;
  cursor: pointer;
`;

/* ---- Intro splash ---- */
export const BBIntroRoot = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #f9f9f9;
`;

export const BBIntroScroll = styled.div`
  flex: 1 1 auto;
`;

export const BBIntroHeader = styled.div`
  max-width: 900px;
  margin: 16px auto 0;
  text-align: center;
  padding: 16px 20px;
  background-color: #ffffff;
  border: 1px solid ${BORDER};
  box-sizing: border-box;
`;

export const BBIntroTabs = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 0.9375rem;
`;

export const BBIntroTab = styled.span`
  color: ${TEXT};
  font-weight: 400;
  &[data-active='true'] {
    color: ${GREEN};
    font-weight: 600;
  }
`;

export const BBIntroTabSep = styled.span`
  color: #d9d9d9;
`;

export const BBIntroTitle = styled.h1`
  font-family: ${TITLE_FONT};
  font-size: 1.5rem;
  font-weight: 600;
  line-height: 1.875rem;
  color: ${TITLE};
  margin: 0 0 6px 0;
`;

export const BBIntroSubtitle = styled.p`
  font-size: 0.9375rem;
  color: ${TEXT};
  line-height: 1.5;
  margin: 0;
`;

export const BBIntroBody = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 16px 0;
  width: 100%;
  box-sizing: border-box;
`;

export const BBIntroBlocks = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const BBIntroBlock = styled.div`
  border: 1px solid ${BORDER};
  padding: 12px 16px;
  background-color: #ffffff;
`;

export const BBIntroBlockTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: ${BLACK};
  margin: 0 0 8px 0;
`;

export const BBIntroTitleIcon = styled.span`
  color: ${GREEN};
  margin-right: 8px;
`;

export const BBIntroOptional = styled.span`
  color: ${MUTED};
  font-weight: 400;
  font-size: 0.8125rem;
`;

export const BBIntroBlockText = styled.p`
  font-size: 0.875rem;
  color: ${TEXT};
  line-height: 1.5;
  margin: 0;
`;

export const BBIntroInfo = styled.div`
  background-color: #f4f4f2;
  border: 1px solid ${BORDER};
  padding: 10px 16px;
  margin-top: 12px;
  font-size: 0.875rem;
  color: ${TEXT};
  line-height: 1.5;
  strong {
    color: ${BLACK};
  }
`;

export const BBIntroBefore = styled.div`
  margin-top: 16px;
`;

export const BBIntroBeforeTitle = styled.h4`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${BLACK};
  margin: 0 0 8px 0;
`;

export const BBIntroCards = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const BBIntroCard = styled.div`
  border: 1px solid ${BORDER};
  padding: 12px;
  background-color: #ffffff;
  cursor: pointer;
  transition: border-color 0.3s ease, box-shadow 0.3s ease,
    background-color 0.3s ease;

  &[role='button']:hover {
    border-color: ${GREEN};
    background-color: #f6faf7;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

export const BBIntroCardIcon = styled.div`
  font-size: 1rem;
  margin-bottom: 8px;
  color: ${GREEN};
`;

export const BBIntroCardTitle = styled.div`
  font-size: 0.9375rem;
  font-weight: 600;
  color: ${BLACK};
  margin-bottom: 4px;
`;

export const BBIntroCardText = styled.div`
  font-size: 0.8125rem;
  color: ${TEXT};
  line-height: 1.4;
`;

export const BBIntroFooter = styled.div`
  border-top: 1px solid ${BORDER};
  background-color: #f9f9f9;
  padding: 12px 20px;
`;

export const BBIntroFooterInner = styled.div`
  max-width: 900px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;

export const BBIntroFooterText = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  color: ${TEXT};
  strong {
    color: ${BLACK};
    margin-bottom: 2px;
  }
`;

export const BBIntroFooterAction = styled.div`
  text-align: right;
`;

export const BBIntroTerms = styled.p`
  font-size: 0.75rem;
  color: ${MUTED};
  margin: 8px 0 0 0;
`;

/* ---- Welcome stepper ---- */
export const BBWelcomeRoot = styled.div`
  min-height: 100vh;
  background-color: #f9f9f9;
  padding: 32px 20px;
  box-sizing: border-box;
`;

export const BBWelcomeHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

export const BBWelcomeTitle = styled.h1`
  font-family: ${TITLE_FONT};
  font-size: 1.5rem;
  font-weight: 600;
  color: ${TITLE};
  margin: 0 0 4px 0;
`;

export const BBWelcomeSubtitle = styled.p`
  font-size: 0.9375rem;
  color: ${TEXT};
  margin: 0;
`;

export const BBWelcomeCard = styled.div`
  max-width: 760px;
  margin: 0 auto;
  background-color: #ffffff;
  border: 1px solid ${BORDER};
  box-sizing: border-box;
`;

export const BBWelcomeStep = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  border-left: 3px solid transparent;
  &[data-active='true'] {
    border-left-color: ${GREEN};
  }
  &:last-child {
    border-bottom: none;
  }
`;

export const BBWelcomeStepInfo = styled.div``;

export const BBWelcomeStepTitle = styled.h3`
  font-size: 0.9375rem;
  font-weight: 600;
  color: #b0b4b8;
  margin: 0 0 4px 0;
  &[data-active='true'] {
    color: ${BLACK};
  }
`;

export const BBWelcomeStepDesc = styled.p`
  font-size: 0.8125rem;
  color: #b0b4b8;
  line-height: 1.4;
  margin: 0;
  &[data-active='true'] {
    color: ${TEXT};
  }
`;

export const BBWelcomeStepNote = styled.p`
  font-size: 0.8125rem;
  color: ${GREEN};
  margin: 6px 0 0 0;
`;

export const BBWelcomeStepMuted = styled.p`
  font-size: 0.75rem;
  color: #b0b4b8;
  margin: 6px 0 0 0;
`;

export const BBWelcomeStepAction = styled.div`
  flex-shrink: 0;
`;
